/**
 * V4 企业看板真实 API（接 backend M14-M17）。
 *
 * 契约：docs/archive/v4/V4-API-dashboard.yaml
 * 后端 endpoint:
 *   GET /api/dashboard/classes/                         M14 list
 *   GET /api/dashboard/classes/{id}/                    M14 retrieve（聚合总览）
 *   GET /api/dashboard/classes/{id}/heatmap/?sort=...   M15
 *   GET /api/dashboard/classes/{id}/risks/              M16
 *   GET /api/dashboard/classes/{id}/training-recommendation/?refresh=true M17
 *
 * 企业级全员看板（spec docs/spec/2026-06-08-企业级全员看板-design.md）：
 *   GET /api/dashboard/enterprises/                              选择器列表（admin/超管）
 *   GET /api/dashboard/enterprises/{id}/                         全员总览
 *   GET /api/dashboard/enterprises/{id}/heatmap/?sort=...        全员热力图
 *   GET /api/dashboard/enterprises/{id}/risks/                   全员风险
 *   GET /api/dashboard/enterprises/{id}/training-recommendation/ 全员培训建议
 *   入口纯镜像 AIQ（按 classes_enabled 分流）：全员页 Spec 1 阶段仅裸码企业可达。
 *
 * 权限（前端只是 UX 层提示；后端是真实关卡）：
 * - 学员 → 全 403（不应进入看板）
 * - 讲师 → list 仅自己授课的班；training-recommendation **403**
 * - enterprise_admin / admin / superadmin → 看本企业 / 全部
 */
import client from './client';

// 后端 VALID_HEATMAP_SORTS = ("score_desc", "score_asc", "name") 的镜像。
// 加新值时, 后端 services/dashboard.py 与本常量同步改。
export const HEATMAP_SORTS = {
  SCORE_DESC: 'score_desc',
  SCORE_ASC: 'score_asc',
  NAME: 'name',
};

/** 把 axios 错误翻成中文用户态文案。看板各页错误态共用。 */
export function readableApiError(err, fallback = '加载失败') {
  const code = err?.response?.status;
  if (code === 404) return '资源不存在或您无权访问';
  if (code === 403) return '无权访问';
  if (code === 401) return '请重新登录';
  return err?.response?.data?.detail || fallback;
}

/** 班级头部副标题: '企业 · 行业' 共用。 */
export function formatClassSubtitle(cls) {
  const ent = cls?.enterprise || {};
  return `${ent.name || ''} · ${ent.industry || '未指定行业'}`;
}

export const listClasses = (params) =>
  client.get('/dashboard/classes/', { params }).then((r) => r.data);

/**
 * default-class-flat:解析"调用者的默认班级",用于 dashboard 入口直跳。
 *  - 讲师 → 自己授课的第一个班
 *  - 企业管理员 → 本企业第一个班(信号自动建的"默认班级")
 *  - admin / superadmin → 404(他们看跨企业,继续走选择器)
 *  - 学员 → 403(后端 IsInstructorOrAbove 拦截)
 */
export const getDefaultClass = () =>
  client.get('/dashboard/classes/default/').then((r) => r.data);

export const getClassDashboard = (id) =>
  client.get(`/dashboard/classes/${id}/`).then((r) => r.data);

export const getClassHeatmap = (id, { sort } = {}) =>
  client.get(`/dashboard/classes/${id}/heatmap/`, { params: sort ? { sort } : {} })
    .then((r) => r.data);

export const getClassRisks = (id) =>
  client.get(`/dashboard/classes/${id}/risks/`).then((r) => r.data);

export const getTrainingRecommendation = (id, { refresh } = {}) =>
  client.get(`/dashboard/classes/${id}/training-recommendation/`, {
    params: refresh ? { refresh: 'true' } : {},
  }).then((r) => r.data);

// ─── 企业级全员看板（spec §4.2）────────────────────────────────────────────
export const listEnterprises = (params = {}) =>
  client.get('/dashboard/enterprises/', { params }).then((r) => r.data);

export const getEnterpriseDashboard = (id) =>
  client.get(`/dashboard/enterprises/${id}/`).then((r) => r.data);

export const getEnterpriseHeatmap = (id, { sort } = {}) =>
  client.get(`/dashboard/enterprises/${id}/heatmap/`, { params: sort ? { sort } : {} })
    .then((r) => r.data);

export const getEnterpriseRisks = (id) =>
  client.get(`/dashboard/enterprises/${id}/risks/`).then((r) => r.data);

export const getEnterpriseTrainingRecommendation = (id, { refresh } = {}) =>
  client.get(`/dashboard/enterprises/${id}/training-recommendation/`, {
    params: refresh ? { refresh: 'true' } : {},
  }).then((r) => r.data);

/** 企业头部副标题: '行业 · 全员'。 */
export function formatEnterpriseSubtitle(ent) {
  return `${ent?.industry || '未指定行业'} · 全员`;
}

// ── 企业报告 PDF 下载（spec §4.3）。路径相对 /api baseURL，勿写 /api/...。 ──
function _filenameFromCD(cd) {
  if (!cd) return null;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  if (star) { try { return decodeURIComponent(star[1]); } catch (_) { /* ignore */ } }
  const plain = /filename="?([^";]+)"?/i.exec(cd);
  return plain ? plain[1] : null;
}

/**
 * 下载企业报告 PDF。后端按 enterprise.classes_enabled 自动分发 R2/R3 形态。
 * 复用个人报告 PDF 同款：blob + 120s timeout（Playwright 冷跑慢）+ Blob 错误文本化。
 * @returns {Promise<{ok: boolean, error?: string}>}
 */
export async function downloadEnterpriseReport(enterpriseId) {
  try {
    const resp = await client.get(`/enterprises/${enterpriseId}/report/download/pdf/`, {
      responseType: 'blob',
      timeout: 120000,
    });
    const blob = new Blob([resp.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const cd = resp.headers?.['content-disposition'] || resp.headers?.['Content-Disposition'];
    a.download = _filenameFromCD(cd) || '企业报告.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return { ok: true };
  } catch (err) {
    let detail = null;
    const respData = err?.response?.data;
    if (respData instanceof Blob) {
      try { detail = JSON.parse(await respData.text())?.detail || null; } catch (_) { detail = null; }
    } else if (respData && typeof respData === 'object') {
      detail = respData.detail || null;
    }
    return { ok: false, error: detail || err?.message || '企业报告下载失败' };
  }
}

/**
 * 下载班级报告 PDF（@action 路由，无 /report/ 段）。enterprise_admin+ 限定（含培训建议）。
 * 复用企业报告同款：blob + 120s timeout + Blob 错误文本化。
 * @returns {Promise<{ok: boolean, error?: string}>}
 */
export async function downloadClassReport(classId) {
  try {
    const resp = await client.get(`/dashboard/classes/${classId}/download/pdf/`, {
      responseType: 'blob',
      timeout: 120000,
    });
    const blob = new Blob([resp.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const cd = resp.headers?.['content-disposition'] || resp.headers?.['Content-Disposition'];
    a.download = _filenameFromCD(cd) || '班级报告.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return { ok: true };
  } catch (err) {
    let detail = null;
    const respData = err?.response?.data;
    if (respData instanceof Blob) {
      try { detail = JSON.parse(await respData.text())?.detail || null; } catch (_) { detail = null; }
    } else if (respData && typeof respData === 'object') {
      detail = respData.detail || null;
    }
    return { ok: false, error: detail || err?.message || '班级报告下载失败' };
  }
}
